export function getCelebrationTitle(discovery, mastery) {
  const d = Number(discovery) || 0;
  const m = Number(mastery) || 0;
  if (hasMasteryLoss(mastery)) return null;
  if (m > 0 || d > 0) return "Fantastique !";
  return null;
}

export function hasMasteryLoss(mastery) {
  const m = Number(mastery);
  return Number.isFinite(m) && m < 0;
}

export function hasPositiveProgress(discovery, mastery) {
  const d = Number(discovery) || 0;
  const m = Number(mastery) || 0;
  return d > 0 || m > 0;
}

/** Seuil (sur 15) pour qualifier la partie de « belle » sans gain de points. */
const NEUTRAL_GOOD_SCORE_MIN = 10;

export function getNeutralProgressMessage(score, totalQuestions = 15) {
  const s = Number(score);
  const total = Number(totalQuestions) || 15;

  if (!Number.isFinite(s) || s < NEUTRAL_GOOD_SCORE_MIN) {
    return "Chaque partie compte. Persévérez pour atteindre les objectifs du niveau suivant.";
  }

  return "Belle partie. Continuez vos efforts pour franchir la prochaine étape.";
}

export function formatPointsLabel(count, kind) {
  const n = Math.abs(Number(count));
  if (!Number.isFinite(n) || n === 0) return `0 point de ${kind}`;
  const unit = n === 1 ? "point" : "points";
  return `${n} ${unit} de ${kind}`;
}

export function formatPositivePointsLabel(count, kind) {
  const n = Math.abs(Number(count));
  if (!Number.isFinite(n) || n === 0) return "";
  const unit = n === 1 ? "point" : "points";
  return `+${n} ${unit} de ${kind}`;
}

export function formatProgressDelta(value) {
  const num = Number(value);
  if (!Number.isFinite(num) || num === 0) return "0";
  if (num > 0) return `+${num}`;
  return String(num);
}

const MS_PER_DAY = 24 * 60 * 60 * 1000;
const MAX_PLAUSIBLE_GAME_DELTA = 15;

export function getRollingDaysCutoffMs(days = 30, now = Date.now()) {
  return Number(now) - days * MS_PER_DAY;
}

export function getMonthStartMs(now = Date.now()) {
  const date = new Date(now);
  date.setDate(1);
  date.setHours(0, 0, 0, 0);
  return date.getTime();
}

function toPlayedAtMs(point) {
  if (!point?.playedAt) return NaN;
  return new Date(point.playedAt).getTime();
}

function isProgressDiscontinuity(delta) {
  return !Number.isFinite(delta) || Math.abs(delta) > MAX_PLAUSIBLE_GAME_DELTA;
}

/**
 * Variation nette d'un compteur de courbe (maîtrise / découverte) après `cutoffMs`.
 * Ignore les sauts impossibles en une partie (changement de niveau / de pool).
 */
export function computeProgressCountDelta(history, cutoffMs, field) {
  const points = (Array.isArray(history) ? history : [])
    .map((point) => ({
      at: toPlayedAtMs(point),
      value: Number(point?.[field]),
    }))
    .filter((point) => Number.isFinite(point.at) && Number.isFinite(point.value))
    .sort((a, b) => a.at - b.at);

  if (!points.length) return null;

  let baseline = null;
  const after = [];
  for (const point of points) {
    if (point.at <= cutoffMs) baseline = point;
    else after.push(point);
  }

  if (!after.length) {
    return baseline ? 0 : null;
  }

  let previous = baseline;
  let total = 0;
  for (const point of after) {
    if (previous) {
      const delta = point.value - previous.value;
      if (!isProgressDiscontinuity(delta)) {
        total += delta;
      }
    } else if (!isProgressDiscontinuity(point.value)) {
      total += point.value;
    }
    previous = point;
  }

  return total;
}

export function getProgressDeltaClassName(value) {
  const num = Number(value);
  if (!Number.isFinite(num) || num === 0) return "progress-delta--zero";
  if (num > 0) return "progress-delta--positive";
  return "progress-delta--negative";
}

export function formatAverageScore(value) {
  if (value == null || Number.isNaN(value)) return "—";
  return Number(value).toFixed(1).replace(".", ",");
}

export function formatPlayTime(ms) {
  const totalMs = Number(ms);
  if (!Number.isFinite(totalMs) || totalMs <= 0) return "0 min";

  const totalSeconds = Math.round(totalMs / 1000);
  if (totalSeconds < 60) return `${totalSeconds} s`;

  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  if (seconds === 0) return `${minutes} min`;
  return `${minutes} min ${String(seconds).padStart(2, "0")} s`;
}
